import React from 'react';
import { SVGIcon } from '../../SVGIcon/SVGIcon';
import { CircularProgress } from './CircularProgress';
import { RaportHSPEstimationProps } from './RaportHSPEstimation.types';
import clsx from 'clsx';
import { BarChart } from '@/components/BarChart/BarChart';
import { DataTable } from '@/components/DataTable/DataTable';
import { SectionSubtitle } from '@/components/Typography/SectionSubtitle';
import { EstimationCard } from '@/components/RaportHSP/RaportHSPEstimation/EstimationCard';
import { APP_COLORS } from '@/config/colors';

/**
 * HSPEstimation component displays vehicle valuation information in 3 columns:
 * - Current vehicle value with date
 * - New vehicle value
 * - Value loss with percentage and circular progress indicator
 */
export const RaportHSPEstimation: React.FC<RaportHSPEstimationProps> = ({
  evaluation,
  className = '',
}) => {
  // Format currency values
  const formatCurrency = (value: number) =>
    `${value.toLocaleString('pl-PL')} ${evaluation.currency}`;

  // Format date
  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('pl-PL');

  // Calculate current value (base value minus depreciation)
  const currentValue = evaluation.baseCarValue - evaluation.depreciation.value;

  // Prepare data for display
  const displayData = {
    currentValue: {
      amount: formatCurrency(currentValue),
      date: `Wycena z dnia ${formatDate(evaluation.evaluationDate)}`,
    },
    newVehicleValue: {
      amount: formatCurrency(evaluation.newCarValueWithEquipment),
      label: 'Wartość nowego pojazdu z wyposażeniem',
    },
    valueLoss: {
      amount: formatCurrency(evaluation.depreciation.value),
      percentage: evaluation.depreciation.rate,
      label: 'Utrata wartości',
      description:
        'Różnica między wartością bazową pojazdu, a jego aktualną wartością rynkową uwzględniająca deprecjację.',
    },
  };

  return (
    <div className="mt-10">
      <SectionSubtitle title="Aktualna wartość rynkowa" icon="airplane" />
      <div
        className={clsx(
          'mt-10 w-full rounded-card border border-grayBorder bg-white',
          className,
        )}
      >
        <div className="flex h-full flex-col lg:flex-row">
          {/* First Column - Current Value */}
          <div className="flex flex-1 gap-[18px] px-6 py-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-primary">
              <SVGIcon name="price-tag" fill="white" />
            </div>
            <div className="flex flex-col gap-2">
              <div className="font-inter text-[22px] font-extrabold leading-5 text-text-primary">
                {displayData.currentValue.amount}
              </div>
              <div className="font-inter text-base font-normal leading-[18px] text-grayText">
                {displayData.currentValue.date}
              </div>
            </div>
          </div>

          {/* First Divider */}
          <div className="h-px w-full bg-grayBorder lg:block lg:w-px"></div>

          {/* Second Column - New Vehicle Value */}
          <div className="flex flex-1 gap-[14px] px-6 py-6">
            <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary-100">
              <SVGIcon name="warning-in-figure" fill="primary" size={20} />
            </div>
            <div className="flex flex-col">
              <div className="font-inter text-sm font-normal leading-[16px] text-grayText">
                {displayData.newVehicleValue.label}
              </div>
              <div className="mt-1 font-inter text-base font-extrabold leading-5 text-text-primary">
                {displayData.newVehicleValue.amount}
              </div>
            </div>
          </div>

          {/* Second Divider */}
          <div className="h-px w-full bg-grayBorder lg:block lg:w-px"></div>

          {/* Third Column - Value Loss */}
          <div className="relative flex flex-1 flex-col flex-wrap items-start gap-4 px-6 py-6 md:flex-row">
            {/* Left side - Icon and info */}
            <div className="flex flex-1 flex-col">
              <div className="flex items-center gap-[14px]">
                <div className="flex h-9 w-9 items-center justify-center rounded-[10px] bg-primary-100">
                  <SVGIcon name="chart-arrow" fill="primary" />
                </div>
                <div className="flex flex-col">
                  <div className="font-inter text-sm font-normal leading-[14px] text-grayText">
                    {displayData.valueLoss.label}
                  </div>
                  <div className="mt-1 font-inter text-base font-extrabold leading-5 text-text-primary">
                    {displayData.valueLoss.amount}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mt-6 max-w-[273px] font-inter text-[13px] font-normal leading-[18px] text-grayText">
                {displayData.valueLoss.description}
              </div>
            </div>

            {/* Right side - Circular progress and percentage */}
            <div className="mx-auto mt-2 flex flex-col items-center lg:right-6 lg:top-6">
              <div className="relative">
                <CircularProgress
                  percentage={displayData.valueLoss.percentage}
                  size={140}
                  strokeWidth={3}
                  color="#0064FF"
                  backgroundColor="#ECF1F9"
                />
                {/* Percentage in center */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="font-inter text-text-primary">
                    <span className="text-[26px] font-extrabold">
                      {displayData.valueLoss.percentage}
                    </span>
                    <span className="text-base font-extrabold"> %</span>
                  </div>
                  <div className="mt-1 font-inter text-[11px] font-normal text-grayText">
                    Utrata wartości
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-6 mt-10 font-inter text-[16px] font-bold leading-[1.27] text-text-primary">
        Czynniki wpływające na wartość pojazdu
      </div>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <EstimationCard
          percentage="-79%"
          variant="red"
          title="Utrata wartości"
          price="-79 837 PLN"
          description="Kluczowe czynniki: Wysoki przebieg, Historia kolizji, Brak serwisu autoryzowanego"
        />
        <EstimationCard
          percentage="20%"
          variant="green"
          title="Wyposażenie dodatkowe"
          price="+4 230 PLN"
          description=""
        />
      </div>

      <div className="mt-16 pt-8">
        <SectionSubtitle title="Zmiana wartości pojazdu w czasie" />
        <div className="mt-16 flex w-full flex-row flex-wrap gap-6">
          <div className="w-1/2 flex-1">
            <BarChart
              data={[
                {
                  label: '2020',
                  value: 150000,
                  color: APP_COLORS.primary.DEFAULT,
                  topLabel: '150k km',
                },
                {
                  label: '2025',
                  value: 241000,
                  color: APP_COLORS.primary.DEFAULT,
                  topLabel: '1320 km',
                },
              ]}
              height={500}
              showGrid={true}
              showValues={true}
              showTopLabels={true}
              valueFormat="PLN"
              responsive={true}
              showLegendBelow={false}
            />
          </div>

          <div className="w-1/2 flex-1">
            <DataTable
              data={[
                { id: 1, label: 'Moc silnika (kW)', value: '115' },
                {
                  id: 2,
                  label: 'Paliwo',
                  value: 'benzyna, gaz płynny (propan butan)',
                },
              ]}
              className="mb-6"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
